import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  Divider,
  DialogContent,
  Grid,
  Typography,
  makeStyles,
  DialogActions,
  Button,
} from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { usersService } from "../../services";
import { UserRole } from "../../domain/userRole";
import { Error } from "../common/Error";
import { useDispatch } from "react-redux";
import { SocItem } from "../../domain/SocItem";
import { sendingItem } from "../../redux/actions";
import { modelToJsonPatchDocument } from "@ofqual/portal-core/core/utils/modelHelpers";

type SendDialogProps = {
  newModel: SocItem;
  dirtyFields: any;
  soc: SocItem;
  isOpen: boolean;
  onClose: () => void;
};

const useStyles = makeStyles(() => ({
  dialogActions: {
    padding: 15,
  },
  body: {
    marginTop: 5,
    marginBottom: 5,
  },
}));

const EMAIL_REGEX = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

export const SendDialog: React.FC<SendDialogProps> = ({
  newModel,
  dirtyFields,
  soc,
  isOpen,
  onClose,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    errors,
    setError,
    formState: { isSubmitted },
    clearErrors,
  } = useForm({
    defaultValues: {
      selectedChairUpn: null,
      chairUpn: null,
    },
  });

  const { selectedChairUpn, chairUpn } = useWatch({
    control,
    name: ["selectedChairUpn", "chairUpn"],
  });

  const [usersLookups] = useLookup({
    service: usersService,
    id: "alternateEmail",
    label: "upn",
    params: {
      role: [UserRole.AoChair],
      active: true,
    },
  });

  const onSubmit = (data: { selectedChairUpn: string; chairUpn: string }) => {
    const chairUpn = data.selectedChairUpn || data.chairUpn;
    const changedFields = Object.keys(dirtyFields) as (keyof SocItem)[];
    let document = modelToJsonPatchDocument(newModel, changedFields);
    dispatch(sendingItem(soc.id, document, chairUpn));
    onClose();
  };

  const isUpnRequired = isSubmitted && !chairUpn && !selectedChairUpn;

  useEffect(() => {
    if (isUpnRequired)
      setError("chairUpn", {
        message: "You must select or enter the Chairs email address",
      });
    else clearErrors("chairUpn");
  }, [setError, isUpnRequired, clearErrors]);

  return (
    <Dialog maxWidth="md" fullWidth open={isOpen} onClose={onClose}>
      <DialogTitle data-test="send-dialog-title">Send SoC</DialogTitle>
      <Divider />
      <form>
        <DialogContent style={{ minHeight: "250px" }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                style={{ marginBottom: "2px", marginTop: "0px" }}
              >
                You must share the SoC with your Awarding Organisations Chair
                before sending
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="selectedChairUpn"
                as={
                  <Select
                    label="Select Awarding Organisations Chair"
                    datatest="select-chair-upn"
                    disabled={chairUpn}
                  >
                    {usersLookups}
                  </Select>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="chairUpn"
                rules={{
                  validate: {
                    regex: (value: string) => value && EMAIL_REGEX.test(value),
                  },
                }}
                as={
                  <TextField
                    label="Can't find your Awarding Organisations Chair? Enter email address"
                    data-test="chair-upn-textfield"
                    disabled={selectedChairUpn}
                  />
                }
              />
              {errors.chairUpn?.type === "regex" && (
                <Error data-test="invalid-upn-error">
                  Please enter a valid email address
                </Error>
              )}
            </Grid>
            {errors.chairUpn && (
              <Grid item xs={12}>
                <Error data-test="submit-error">
                  {errors.chairUpn.message}
                </Error>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={onClose}
            color="primary"
            data-test="close-send-button"
          >
            Close
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            color="primary"
            data-test="confirm-send-button"
          >
            Send
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
